// Groups — the selection list (GET /groups/selection) feeding pickers like the
// common MembersPicker, plus the People page's Groups tab: sections with their
// embedded groups (GET /group-sections) and the section (create / rename /
// delete) + group (create / update / move / delete) mutations. Moving a group
// between sections has its own endpoint (PATCH /groups/:id/move) — it is not a
// partial body on the group update.

import { baseApi } from '../baseApi';
import { ROUTES } from '../routes';

export interface GroupOption {
  id: string;
  name: string;
  membersCount: number;
}

interface ApiGroupOption {
  _id: string;
  groupName: string;
  users?: unknown[] | null;
}

// ---- Groups tab: sections + embedded groups ----
export interface GroupMember {
  id: string;
  firstName: string;
  lastName: string;
  fullName: string;
}
export interface GroupItem {
  id: string;
  name: string;
  allowJoinByLink: boolean;
  members: GroupMember[];
  membersCount: number;
}
export interface GroupSection {
  id: string;
  name: string;
  isDefault: boolean;
  groups: GroupItem[];
}

export interface CreateGroupInput {
  groupName: string;
  groupSection: string;
  users: string[];
  allowJoinByLink: boolean;
}

/** PATCH /groups/:id/move body — the section is the only field a move changes,
 *  under the same `groupSection` key the create payload uses. */
export interface MoveGroupInput {
  groupSection: string;
}

export interface UpdateGroupInput {
  groupName: string;
  users: string[];
  allowJoinByLink: boolean;
}

// ---- raw shapes (per the confirmed response) ----
interface ApiGroupUser {
  /** The COMPANY-USER id — the id space the selection list and the write
   *  payloads use. The nested `user._id` is the User id and matches nothing
   *  the pickers key off; only the name comes from there. */
  _id: string;
  user?: { _id: string; firstName?: string; lastName?: string } | null;
}
interface ApiGroup {
  _id: string;
  groupName: string;
  allowJoinByLink?: boolean;
  users?: ApiGroupUser[] | null;
}
interface ApiGroupSection {
  _id: string;
  sectionName: string;
  isDefault?: boolean;
  groups?: ApiGroup[] | null;
}

const toMember = (u: ApiGroupUser): GroupMember => {
  const firstName = u.user?.firstName ?? '';
  const lastName = u.user?.lastName ?? '';
  return {
    id: u._id,
    firstName,
    lastName,
    fullName: `${firstName} ${lastName}`.trim(),
  };
};
const toGroup = (g: ApiGroup): GroupItem => {
  const members = (g.users ?? []).map(toMember);
  return {
    id: g._id,
    name: g.groupName,
    allowJoinByLink: !!g.allowJoinByLink,
    members,
    membersCount: members.length,
  };
};
const toSection = (s: ApiGroupSection): GroupSection => ({
  id: s._id,
  name: s.sectionName,
  isDefault: !!s.isDefault,
  groups: (s.groups ?? []).map(toGroup),
});

export const groupsApi = baseApi.injectEndpoints({
  endpoints: (build) => ({
    getGroupsSelection: build.query<GroupOption[], void>({
      query: () => ({ ...ROUTES.groups.selection }),
      transformResponse: (res: ApiGroupOption[]) =>
        (res ?? []).map((g) => ({
          id: g._id,
          name: g.groupName,
          membersCount: g.users?.length ?? 0,
        })),
    }),
    getGroupSections: build.query<GroupSection[], void>({
      query: () => ({ ...ROUTES.groupSections.list }),
      transformResponse: (res: ApiGroupSection[]) => (res ?? []).map(toSection),
      providesTags: [{ type: 'Groups', id: 'LIST' }],
    }),
    createGroupSection: build.mutation<{ _id?: string }, { sectionName: string }>({
      query: (body) => ({ ...ROUTES.groupSections.create, data: body }),
      invalidatesTags: [{ type: 'Groups', id: 'LIST' }],
    }),
    updateGroupSection: build.mutation<unknown, { id: string; body: { sectionName: string } }>({
      query: ({ id, body }) => ({ ...ROUTES.groupSections.update(id), data: body }),
      invalidatesTags: [{ type: 'Groups', id: 'LIST' }],
    }),
    deleteGroupSection: build.mutation<unknown, string>({
      query: (id) => ({ ...ROUTES.groupSections.remove(id) }),
      invalidatesTags: [{ type: 'Groups', id: 'LIST' }],
    }),
    createGroup: build.mutation<{ _id?: string }, CreateGroupInput>({
      query: (body) => ({ ...ROUTES.groups.create, data: body }),
      invalidatesTags: [{ type: 'Groups', id: 'LIST' }],
    }),
    updateGroup: build.mutation<unknown, { id: string; body: UpdateGroupInput }>({
      query: ({ id, body }) => ({ ...ROUTES.groups.update(id), data: body }),
      invalidatesTags: [{ type: 'Groups', id: 'LIST' }],
    }),
    // Reparents one group. The sections query is the only consumer of the
    // placement, so the same coarse LIST invalidation refreshes the whole tree.
    moveGroup: build.mutation<unknown, { id: string; body: MoveGroupInput }>({
      query: ({ id, body }) => ({ ...ROUTES.groups.move(id), data: body }),
      invalidatesTags: [{ type: 'Groups', id: 'LIST' }],
    }),
    deleteGroup: build.mutation<unknown, string>({
      query: (id) => ({ ...ROUTES.groups.remove(id) }),
      invalidatesTags: [{ type: 'Groups', id: 'LIST' }],
    }),
  }),
});

export const {
  useGetGroupsSelectionQuery,
  useGetGroupSectionsQuery,
  useCreateGroupSectionMutation,
  useUpdateGroupSectionMutation,
  useDeleteGroupSectionMutation,
  useCreateGroupMutation,
  useUpdateGroupMutation,
  useMoveGroupMutation,
  useDeleteGroupMutation,
} = groupsApi;
